import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Form, Icon } from "semantic-ui-react";

import { Element } from "components/ReactScroll/index";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { TYRETEAM_CHANNELS } from "models";
import { TyreTeamCredentialsModal } from "modules/DealerLocationDetails/components/Tyres/components";
import { useTyreTeamCredentialsQuery } from "modules/DealerLocationDetails/components/Tyres/hooks";
import { ASTERISK_PLACEHOLDER } from "modules/DealerOverviewDashboard/components";
import { isAdmin } from "utils/common";

import type { DealerLocation, TyreTeamCredential } from "models";
import type { LocationAdminFormPayload } from "modules/DealerLocationDetails";

const FormHelper = createFormHelper<LocationAdminFormPayload>();

type ThresholdRow = {
  label: string;
  fallback: string;
  advice: "all_season_a_threshold" | "summer_a_threshold" | "winter_a_threshold" | "truck_tyre_a_threshold";
  critical: "all_season_n_threshold" | "summer_n_threshold" | "winter_n_threshold" | "truck_tyre_n_threshold";
};

type ThresholdSection = {
  label: string;
  fallback: string;
  icon: string;
  rows: ThresholdRow[];
};

const THRESHOLD_SECTIONS: ThresholdSection[] = [
  {
    label: "v8_all_season_tyre_settings",
    fallback: "All Season tyre settings",
    icon: "road",
    rows: [{ label: "v8_all_season", fallback: "All season", advice: "all_season_a_threshold", critical: "all_season_n_threshold" }]
  },
  {
    label: "v8_summer_tyre_settings",
    fallback: "Summer tyre settings",
    icon: "sun",
    rows: [{ label: "v8_summer_tyres", fallback: "Summer tyres", advice: "summer_a_threshold", critical: "summer_n_threshold" }]
  },
  {
    label: "v8_winter_tyre_settings",
    fallback: "Winter tyre settings",
    icon: "snowflake",
    rows: [{ label: "v8_winter_tyres", fallback: "Winter tyres", advice: "winter_a_threshold", critical: "winter_n_threshold" }]
  },
  {
    label: "v8_truck_tyre_settings",
    fallback: "Truck tyre settings",
    icon: "truck",
    rows: [{ label: "v8_truck_tyres", fallback: "Truck tyres", advice: "truck_tyre_a_threshold", critical: "truck_tyre_n_threshold" }]
  }
];

const CHANNEL_LABELS = new Map([
  [TYRETEAM_CHANNELS.WheelTyre, "Wheel-Tyre"],
  [TYRETEAM_CHANNELS.BandenExpress, "Banden Express"]
]);

type TyresProps = {
  tabId: string;
  location: DealerLocation;
};

export const Tyres = ({ tabId, location }: TyresProps) => {
  const { t } = useTranslation();

  const [credentialToEdit, setCredentialToEdit] = useState<TyreTeamCredential | null>(null);
  const [isCredentialsModalOpen, setIsCredentialsModalOpen] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { watch, setValue, formState } = FormHelper.useFormContext();
  const [isTyreTeamEnabled, isTyreNetEnabled] = watch(["is_tyre_team_enabled", "is_tyre_net_enabled"]);

  const isAdminUser = Boolean(user && isAdmin(user.role_id));
  const { data: credentials = [] } = useTyreTeamCredentialsQuery(location.id);

  const handleOpenCredentialsModal = (credential: TyreTeamCredential | null) => {
    setCredentialToEdit(credential);
    setIsCredentialsModalOpen(true);
  };

  const handleCloseCredentialsModal = () => {
    setIsCredentialsModalOpen(false);
    setCredentialToEdit(null);
  };

  return (
    <Element name={tabId} className="dealer-location-section">
      <div className="section-title">
        <div>
          <Icon className="tire" />
        </div>
        <span className="section-title-text">{t("v8_tyres", "Tyres")}</span>
      </div>

      <div className="dealer-location-columns dealer-location-columns-paired">
        {THRESHOLD_SECTIONS.map(section => (
          <div key={section.label} className="dealer-location-column">
            <div className="dealer-location-subheading">
              <Icon className={section.icon} />
              {t(section.label, section.fallback)}
            </div>
            {section.rows.map(row => {
              const hasError = watch(row.critical) > watch(row.advice);

              return (
                <div key={row.advice}>
                  <Form.Field error={hasError}>
                    <label>
                      {t(row.label, row.fallback)} - {t("v8_advice_threshold", "Advice threshold")}
                    </label>
                    <FormHelper.Field.Input name={row.advice} inputProps={{ type: "number", step: 0.1, min: 0 }} />
                  </Form.Field>
                  <Form.Field error={hasError}>
                    <label>
                      {t(row.label, row.fallback)} - {t("v8_critical_threshold", "Critical threshold")}
                    </label>
                    <FormHelper.Field.Input name={row.critical} inputProps={{ type: "number", step: 0.1, min: 0 }} />
                    {hasError && <div className="dealer-location-field-error">{t("v8_threshold_validation", "Threshold N can't be bigger than threshold A")}</div>}
                  </Form.Field>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {isAdminUser && (
        <>
          <Divider />
          <div className="dealer-location-columns">
            <div className="dealer-location-column">
              <div className="dealer-location-subheading">{t("v8_tyre_configuration", "Tyre configuration")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="is_tyre_team_enabled"
                  label={t("v8_tyre_team", "Tyre team")}
                  onChange={enabled => enabled && setValue("is_tyre_net_enabled", false)}
                />
              </Form.Field>
              <Form.Field>
                <FormHelper.Field.Checkbox
                  name="is_tyre_net_enabled"
                  label={t("v8_tyrenet", "Tyrenet")}
                  onChange={enabled => enabled && setValue("is_tyre_team_enabled", false)}
                />
              </Form.Field>

              {isTyreTeamEnabled && (
                <>
                  <Form.Field>
                    <FormHelper.Field.Checkbox
                      name="is_tyre_team_autofill_price_ref_on_order"
                      label={t("v8_autofill_price_ref_on_order", "Autofill price ref on order")}
                    />
                  </Form.Field>
                  <div className="dealer-location-credentials">
                    {credentials.map(credential => (
                      <div key={credential.id} className="dealer-location-credential" onClick={() => handleOpenCredentialsModal(credential)}>
                        {CHANNEL_LABELS.get(credential.channel) ?? t("v8_unknown_channel", "Unknown channel")}
                      </div>
                    ))}
                    {!credentials.length && <div>{t("v8_no_credentials_added", "No credentials added")}</div>}
                  </div>
                  <Button type="button" className="light" onClick={() => handleOpenCredentialsModal(null)}>
                    {t("v8_add_new_credentials", "Add new credentials")}
                    <Icon className="plus" />
                  </Button>
                </>
              )}

              {isTyreNetEnabled && (
                <>
                  <Form.Field required error={formState.isSubmitted && !location.has_tyre_net_tic_uid && !watch("tyre_net_tic_uid")}>
                    <label>{t("v8_tyre_net_tic_uid", "Tyrenet TIC UID")}</label>
                    <FormHelper.Field.Input
                      name="tyre_net_tic_uid"
                      preventAutofill
                      inputProps={{ type: "password", placeholder: location.has_tyre_net_tic_uid ? ASTERISK_PLACEHOLDER : "" }}
                    />
                  </Form.Field>
                  <Form.Field required error={formState.isSubmitted && !location.has_tyre_net_tic_id && !watch("tyre_net_tic_id")}>
                    <label>{t("v8_tyre_net_tic_id", "Tyrenet TIC ID")}</label>
                    <FormHelper.Field.Input
                      name="tyre_net_tic_id"
                      preventAutofill
                      inputProps={{ type: "password", placeholder: location.has_tyre_net_tic_id ? ASTERISK_PLACEHOLDER : "" }}
                    />
                  </Form.Field>
                </>
              )}
            </div>

            <div className="dealer-location-column">
              <div className="dealer-location-subheading">{t("v8_tyre_motion_ai", "Tyre Motion AI")}</div>
              <Form.Field>
                <FormHelper.Field.Checkbox name="is_tyre_motion_enabled" label={t("v8_scan_tyre_data", "Scan tyre data")} />
              </Form.Field>
            </div>
          </div>
        </>
      )}

      <TyreTeamCredentialsModal isOpen={isCredentialsModalOpen} locationId={location.id} credential={credentialToEdit} onClose={handleCloseCredentialsModal} />
    </Element>
  );
};
